import {
  Box,
  Button,
  Flex,
  FormControl,
  FormLabel,
  Icon,
  IconButton,
  Input,
  InputGroup,
  InputRightElement,
  keyframes,
} from "@chakra-ui/react"
import { Logo } from "component/Logo"
import { notify } from "misc/notifications"
import { FC, FormEvent, useState } from "react"
import { FiEye, FiEyeOff } from "react-icons/fi"
import { ToastContainer } from "react-toastify"

interface IAuth {
  // Текст ошибки входа или undefined, если вошли
  signIn: (email: string, password: string) => Promise<string | undefined>
}

// До входа общий контейнер уведомлений (App) не смонтирован — у формы свой,
// сверху по центру. После входа формы нет, уведомления страниц не задеты
const AUTH_TOASTS = "auth"

// Цветные пятна за стеклом: медленно плавают (без анимации — если она
// выключена в системе)
const float = keyframes`
  0% { transform: translate(0, 0) scale(1); }
  50% { transform: translate(40px, -30px) scale(1.08); }
  100% { transform: translate(-30px, 20px) scale(0.95); }
`

// Вокруг карточки (она по центру), чтобы краями заходили под стекло.
// За лого — голубое: на фиолетовом пятне фиолетовое лого сливалось
const BLOBS = [
  { color: "#90CDF4", size: 440, top: "34%", left: "44%", duration: 22 },
  { color: "#FBB6CE", size: 380, top: "48%", left: "62%", duration: 16 },
  { color: "#B794F4", size: 460, top: "68%", left: "45%", duration: 18 },
]

// Стеклянная рамка — едва заметная, у карточки и у полей одинаковая
const GLASS_BORDER = "rgba(255, 255, 255, 0.4)"

// Поля — тоже стекло. Своё размытие у поля бесполезно: под ним уже
// стеклянная карточка, а не фон страницы. Каждый слой белого выбеливает
// цвет (карточка 45% + поле 55% давали серый), поэтому поле почти прозрачное,
// а края задаёт рамка
const fieldProps = {
  size: "lg",
  bg: "rgba(255, 255, 255, 0.3)",
  borderColor: GLASS_BORDER,
  _hover: { borderColor: "rgba(255, 255, 255, 0.7)" },
  // Chrome красит автозаполненное поле в свой голубовато-серый — оставляем
  // наш фон: смена цвета "растягивается" так, что не наступает
  sx: {
    "&:-webkit-autofill, &:-webkit-autofill:hover, &:-webkit-autofill:focus": {
      transition: "background-color 600000s 0s, color 600000s 0s",
    },
  },
}

// Форма входа: карточка из "жидкого стекла" с лого, за ней — цветные пятна
const Auth: FC<IAuth> = ({ signIn }) => {
  const [email, setEmail] = useState("")
  const [password, setPassword] = useState("")
  const [isPasswordVisible, setIsPasswordVisible] = useState(false)
  const [isLoading, setIsLoading] = useState(false)

  const isFilled = !!email.trim() && !!password

  const handleSubmit = async (event: FormEvent) => {
    event.preventDefault()
    setIsLoading(true)
    const error = await signIn(email.trim(), password)
    setIsLoading(false)

    // Почему не пускает: неверный пароль или учётка заблокирована
    if (error) {
      notify(error, "error", {
        containerId: AUTH_TOASTS,
        position: "top-center",
        autoClose: 5000,
      })
    }
  }

  return (
    <Flex
      h="full"
      w="full"
      // Фон на тон темнее карточки, сверху едва заметное свечение в цвет лого
      bg="gray.100"
      bgImage="radial-gradient(ellipse at top, var(--chakra-colors-purple-50), transparent 60%)"
      justify="center"
      align="center"
      px={4}
      position="relative"
      overflow="hidden"
    >
      {BLOBS.map((blob) => (
        <Box
          key={blob.color}
          position="absolute"
          top={blob.top}
          left={blob.left}
          w={`${blob.size}px`}
          h={`${blob.size}px`}
          // Центр пятна — в точке top/left. Отступами, а не transform:
          // transform занят анимацией
          ml={`-${blob.size / 2}px`}
          mt={`-${blob.size / 2}px`}
          borderRadius="full"
          bgImage={`radial-gradient(circle, ${blob.color} 0%, transparent 68%)`}
          opacity={0.75}
          pointerEvents="none"
          sx={{
            animation: `${float} ${blob.duration}s ease-in-out infinite alternate`,
            "@media (prefers-reduced-motion: reduce)": { animation: "none" },
          }}
        />
      ))}

      {/* Жидкое стекло: сквозь сильное размытие видны цветные пятна */}
      <Box
        position="relative"
        zIndex={1}
        w="full"
        maxW="360px"
        bg="rgba(255, 255, 255, 0.45)"
        backdropFilter="blur(24px) saturate(180%)"
        border="1px solid"
        borderColor={GLASS_BORDER}
        borderRadius="xl"
        boxShadow="0 8px 32px rgba(76, 52, 128, 0.12)"
        px={6}
        pt={7}
        pb={6}
      >
        {/* Лого — вместо заголовка "Вход": и так понятно, что это вход */}
        <Flex justify="center">
          <Logo isDark imageSize="208px" />
        </Flex>

        <Flex as="form" direction="column" gap={5} mt={12} onSubmit={handleSubmit}>
          <FormControl>
            {/* Как текст пунктов сайдбара: gray.600 на стекле был блёклым */}
            <FormLabel fontSize="sm" color="gray.700">
              Логин
            </FormLabel>
            <Input
              {...fieldProps}
              autoFocus
              autoComplete="username"
              value={email}
              onChange={(event) => setEmail(event.target.value)}
            />
          </FormControl>

          <FormControl>
            <FormLabel fontSize="sm" color="gray.700">
              Пароль
            </FormLabel>
            <InputGroup>
              <Input
                {...fieldProps}
                type={isPasswordVisible ? "text" : "password"}
                autoComplete="current-password"
                value={password}
                onChange={(event) => setPassword(event.target.value)}
              />
              <InputRightElement h="full">
                <IconButton
                  aria-label={isPasswordVisible ? "Скрыть пароль" : "Показать пароль"}
                  icon={<Icon as={isPasswordVisible ? FiEyeOff : FiEye} />}
                  size="sm"
                  variant="ghost"
                  color="gray.500"
                  onClick={() => setIsPasswordVisible((prev) => !prev)}
                />
              </InputRightElement>
            </InputGroup>
          </FormControl>

          <Button
            type="submit"
            colorScheme="purple"
            size="lg"
            mt={2}
            isLoading={isLoading}
            isDisabled={!isFilled}
          >
            Войти
          </Button>
        </Flex>
      </Box>

      <ToastContainer containerId={AUTH_TOASTS} theme="colored" />
    </Flex>
  )
}

export default Auth
